
    // IMPORTS
    import { computed, defineComponent, onBeforeMount, PropType, ref, watch } from 'vue'
    import { useSparePartFilterStore } from '@/store/sparePartFilter'
    import { PropertyValueDTO } from '@/store/classification'

    import { TreeView, TreeViewExpandChangeEvent, TreeViewCheckChangeEvent } from '@progress/kendo-vue-treeview'

    import { buildLocalization, StringUtil } from '@/util'

    interface IFunctionNode {
        id: string,
        text: string,
        items: IFunctionNode[],
        resultCount: number
    }

    interface IFunctionTreeNode {
        id: string,
        text: string,
        items: IFunctionTreeNode[],
        resultCount: number,
        checked: boolean,
        checkedIndeterminate: boolean,
        expanded: boolean,
    }

    export default defineComponent({
        name: 'sparepartFunctionFilter',

        components: {
            TreeView
        },

        props: {
            /** Optionally speficies the root of the function tree to use (otherwise the complete tree is used) */
            rootFunction: { type: String as PropType<string>, required: false, default: () => '' }
        },

        emits: {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            onChange: (checkedState: string[]) => true
        },

        setup(props, { emit }) {
            /********************************************************************************************************************
             PRIVATE DATA
             ********************************************************************************************************************/
            const sparePartFilterStore = useSparePartFilterStore()

            /** array of ids of currently expanded functions */
            const expandedState = ref<string[]>([])

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            // ids of checked items
            const checkedState = ref<string[]>([])

            const publicData = { checkedState }

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const allFunctionPropertyValues = computed(() => sparePartFilterStore.allNonZeroResultSparePartFunctionPropertyValues)
            const sparepartFunctionStats = computed(() => sparePartFilterStore.sparePartFunctionStats)

            const selectedFunctions = computed(() => sparePartFilterStore.selectedFunctions)           
            const filterIsActive = computed(() => selectedFunctions.value.length  > 0)
            const functionNodes = computed(() => StringUtil.isNullOrEmpty(props.rootFunction)
                ? getTreeNodes(null, allFunctionPropertyValues.value)
                : getTreeNodes(props.rootFunction, allFunctionPropertyValues.value)
            )

            /** Returns all possible tree nodes to show in the function filter */
            const treeNodes = computed(() => processFunctionNodes(functionNodes.value, expandedState.value, checkedState.value))

            const computedData = { filterIsActive, treeNodes }

            /********************************************************************************************************************
             PUBLIC METHODS
             ********************************************************************************************************************/
            const { localize } = buildLocalization()

            function getNodesToExpand(nodes: IFunctionTreeNode[], filter: (node: IFunctionTreeNode) => boolean): string[] {
                const result: string[] = []

                nodes.forEach(node => {
                    const childItems = getNodesToExpand(node.items, filter)

                    if (childItems.length) {
                        result.push(...childItems, node.id)
                    }
                    else if (filter(node)) {
                        result.push(node.id)
                    }
                })

                return result
            }

            function getResultCountString(node: IFunctionTreeNode): string {
                return node.resultCount == null
                    ? `[${localize('loading')}]`
                    : `[${node.resultCount}]`
            }

            function onExpandChange(event: TreeViewExpandChangeEvent) {
                const node = event.item as IFunctionTreeNode
                const index = expandedState.value.indexOf(node.id)

                if (index === -1) {
                    // Add node
                    expandedState.value.push(node.id)
                }
                else {
                    // Remove node
                    expandedState.value.splice(index, 1)
                }
            }

            function onCheckChange(event: TreeViewCheckChangeEvent) {
                const item = event.item as IFunctionTreeNode

                // toggle
                item.checked = !item.checked && item.resultCount > 0

                // auto-toggle all descendants
                descendants(item).forEach(d => d.checked = item.checked)

                // update state
                checkedState.value = getCheckedState(treeNodes.value)

                // apply immediately
                sparePartFilterStore.setSelectedFunctons(checkedState.value)

                // emit updated state
                emit('onChange', checkedState.value)
            }

            function getCheckedState(nodes: IFunctionTreeNode[]): string[] {
                if (!nodes.length) return []

                return [
                    ...nodes.filter(n => n.checked &&                         
                        !n.items.length        // never explicitly check any non-leaf nodes
                        && n.resultCount > 0   // never check any nodes with 0-result
                    )                    
                    .map(n => n.id),
                    ...getCheckedState(nodes.flatMap(n => n.items))
                ]
            }

            function descendants(node: IFunctionTreeNode): IFunctionTreeNode[] {
                const children = node.items
                if (!children.length) return []

                return [...children, ...children.flatMap(child => descendants(child))]
            }

            const publicMethods = {
                localize,
                onExpandChange,                
                onCheckChange,
                getResultCountString
            }

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/
            function initializeState() 
            {
                // copy checked items from user state store
                checkedState.value = [...selectedFunctions.value]

                // auto-expand checked items
                expandedState.value = getNodesToExpand(treeNodes.value, n => n.items.some(n => n.checked) && !n.items.every(n => n.checked))
            }

            function getTreeNodes(parent: string | null, functionPropertyValues: PropertyValueDTO[]): IFunctionNode[] {
                const result = functionPropertyValues
                    .filter(f => f.parentPropertyValue == parent)
                    .sortBy(f => f.propertyValueText)
                    .map(pv => buildNode(pv, functionPropertyValues))

                return result
            }

            function buildNode(pv: PropertyValueDTO, functionPropertyValues: PropertyValueDTO[]): IFunctionNode {
                const childItems = getTreeNodes(pv.propertyValue, functionPropertyValues)
                const resultCount = sparepartFunctionStats.value.get(pv.propertyValue) ?? 0

                return {
                    id: pv.propertyValue,
                    text: removeCurlyBraces(pv.propertyValueText),
                    items: childItems,
                    resultCount: resultCount
                }
            }

            function removeCurlyBraces(s: string) {
                return s.replace(/\{[^{}]*\}/g, '')
            }

            function processFunctionNodes(nodes: IFunctionNode[], expandedNodes: string[], checkedNodes: string[]): IFunctionTreeNode[] {
                return nodes.map(node => {
                    const childItems = processFunctionNodes(node.items, expandedNodes, checkedNodes)
                    const checked = (node.resultCount > 0 && checkedNodes.includes(node.id)) ||                     
                                    (childItems.length > 0 && childItems.some(child => child.checked && child.resultCount > 0) && childItems.every(child => child.checked || child.resultCount == 0))                    
                    const checkedIndeterminate = childItems.some(n => n.checked) && !childItems.every(n => n.checked)

                    return ({
                        ...node,
                        items: childItems,
                        checked: checked,
                        expanded: expandedNodes.includes(node.id),
                        checkedIndeterminate: checkedIndeterminate
                    }) as IFunctionTreeNode
                })
            }

            /********************************************************************************************************************
             LIFE CYCLE
             ********************************************************************************************************************/
            onBeforeMount(() => {
                initializeState()
            })

            // Keep local checked state in sync when the store is reset externally (e.g. via filter chips)
            watch(selectedFunctions, (newVal) => {
                checkedState.value = [...newVal]
            })

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...publicData, ...computedData, ...publicMethods }
        }
    });
